import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { AssetFault } from 'src/app/models/assetFault';
import { ContributionDto, ContributionSourceTypes } from '../../models/contribution';
import { EvidenceCardVm } from '../../models/issue-view';
import { buildEvidenceCard, evidenceCount } from '../../issue-formatting';

// A source report summary with its count and latest evidence time.
@Component({
  selector: 'app-evidence-card',
  templateUrl: './evidence-card.component.html',
  styleUrls: ['./evidence-card.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvidenceCardComponent implements OnChanges {
  @Input() contribution!: ContributionDto;
  // Total firings across the grouped source (Σ fireCount); 1 = a single firing.
  @Input() occurrences = 1;
  // Operator reports only: the reporter resolved from the Issue's activity feed.
  // Null whenever the feed can't name them — the card stays generic rather than
  // guessing.
  @Input() reporterName: string | null = null;
  // The asset's live fault rows. Empty until the lookup answers, and empty for
  // good when it fails; the card renders without the bus line either way.
  @Input() assetFaults: AssetFault[] = [];

  // Raised when the user asks to see what fired this. Only emitted for evidence that
  // carries a source reference; a manual report has nothing behind it to open.
  @Output() openSource = new EventEmitter<ContributionDto>();

  readonly ContributionSourceTypes = ContributionSourceTypes;

  vm!: EvidenceCardVm;
  metaLine = '';
  countLine = '';
  expiryLine = '';
  whenLine = '';

  constructor(private readonly translate: TranslateService) {}

  get linkable(): boolean {
    const c = this.contribution;
    if (c?.sourceType === ContributionSourceTypes.Alert) return !!c.sourceRefAlertId;
    if (c?.sourceType === ContributionSourceTypes.DVIRDefect) return !!c.sourceRefDvirId;
    return false;
  }

  onOpenSource(): void {
    if (this.linkable) this.openSource.emit(this.contribution);
  }

  ngOnChanges(): void {
    const c = this.contribution;
    this.vm = buildEvidenceCard(c);
    this.metaLine = this.vm.codeLine || this.buildMeta();

    const count = evidenceCount(c, this.occurrences);
    this.countLine = count
      ? this.translate.instant(count.key, {
          count: count.reports,
          date: this.shortDate(count.sinceUtc),
        })
      : '';

    this.expiryLine = this.vm.expiry
      ? this.translate.instant(this.vm.expiry.key, {
          days: this.vm.expiry.days,
          date: this.shortDate(this.vm.expiry.atUtc),
        })
      : '';

    this.whenLine = this.vm.whenKey && this.vm.whenAtUtc
      ? this.translate.instant(this.vm.whenKey, { time: this.relative(this.vm.whenAtUtc) })
      : '';


  }

  private get lang(): string {
    return this.translate.currentLang || 'en-US';
  }

  private shortDate(value: string): string {
    return value ? formatDate(value, 'MMM d', this.lang) : '';
  }

  // Same wording ladder the drawer's other timestamps use.
  private relative(value: string): string {
    if (!value) return '';
    const then = new Date(value);
    const min = Math.floor((Date.now() - then.getTime()) / 60000);
    if (min < 1) return this.translate.instant('@issueJustNow');
    if (min < 60) return this.translate.instant('@issueMinutesAgo', { n: min });
    const hours = Math.floor(min / 60);
    if (hours < 24) return this.translate.instant('@issueHoursAgo', { n: hours });
    const days = Math.floor(hours / 24);
    if (days <= 7) return this.translate.instant('@issueDaysAgo', { n: days });
    return formatDate(then, 'shortDate', this.lang);
  }

  // Fallback meta when the contribution carries no fault codes. DVIR: a "Driver
  // inspection" label (driver name isn't on the contribution). Manual: never the
  // slug — an operator report with no text is a report with nothing to quote, and
  // the slug is not operator-facing.
  private buildMeta(): string {
    const c = this.contribution;
    // A DVIR's title is the driver's own words and its pill already names the source,
    // so a third label there would say nothing.
    if (c.sourceType === ContributionSourceTypes.DVIRDefect) return '';
    // An operator writes a title AND a detail, and the detail is the part worth
    // quoting. It is the one source whose title does not already carry its text.
    if (c.sourceType === ContributionSourceTypes.Manual) return c.evidenceText || '';
    return c.evidenceText || c.slug || c.ruleId || '';
  }
}
